package com.starparty.app.astro0.renderers

import com.starparty.app.astro0.*
import com.starparty.app.astro0.celestialbodies.CelestialBody
import com.starparty.app.astro0.celestialbodies.DSO
import com.starparty.app.geometry.SP_Point
import com.starparty.app.geometry.SP_Rectangle

/**
 * Title:        Starparty Astro-0
 * Description:  Simply astronomy package for starparty.com and sennen-cove-cornwall.uk
 * Copyright:    Copyright (c) 2021
 * Company: starparty.com
 * @author Rob Leeson
 * @version 2.0
 */
class DSORenderer : CelestialBodyRenderer() {

    val DIAMETER = 10.0

    /** draws the b as a rectangle */
    override fun paint(b: CelestialBody, labelType:Int, g: SP_Canvas, p: SP_Paint) {

        val dso = b as DSO
        val dsoType = dso.dsoType

        val d = getDiameter(b)

        val x = b.skyLocation.x
        val y = b.skyLocation.y

        p.setColour(getColour(b))

        when (dsoType) {
            "GC"  -> drawGlobularCluster(x, y, d, g, p)
            "OC"  -> drawOpenCluster(x, y, d, g, p)
            "SG"  -> drawGalaxy(x, y, d, g, p)
            "BSG" -> drawGalaxy(x, y, d, g, p)
            "DEG" -> drawGalaxy(x, y, d, g, p)
            else -> drawDefaultDSO(x, y, d, g, p)
        }

        paintTitle(b, d, labelType, g, p)
    }

    fun drawDefaultDSO(x:Double, y:Double, d:Double, g:SP_Canvas, p:SP_Paint) {
        val r = (d / 2.0)
        val left = x - r
        val top = y - r

        val rect = SP_Rectangle(left, top, d, d)

        g.drawRectUnfilled(rect, p)
    }

    fun drawGalaxy(x:Double, y:Double, d:Double, g:SP_Canvas, p:SP_Paint) {
        val d1 = (d * 1.2)
        val d2 = (d * 0.8)

        val left = x - (d1 / 2.0)
        val top = y - (d2 / 2.0)

        val rect = SP_Rectangle(left, top, d1, d2)

        g.drawOvalUnfilled(rect, p)
    }

    fun drawOpenCluster(x:Double, y:Double, d:Double, g:SP_Canvas, p:SP_Paint) {
        val r = (d / 2.0)
        g.drawCircleUnfilled(x, y, r, p)
    }

    fun drawGlobularCluster(x:Double, y:Double, d:Double, g:SP_Canvas, p:SP_Paint) {
        val r = (d / 2.0)
        g.drawCircleFilled(x, y, r, p)
    }

    fun getToolTipText(d: DSO, sky: Sky): String {
        val equ = d.equatorialCoords
        val altAz = sky.EquToAltAzm(equ.rA, equ.dec)
        val D = "deg "
        val DM = "' "
        val DS = "\""
        val H = "h "
        val M = "m "
        val S = "s"
        val DP = 1 // Number of decimal places.
        val raHMS: String = UMS.degreesToHMS(equ.rA).toString(DP, H, M, S)
        val decDMS: String =
            UMS.degreesToDMS(equ.dec).toString(DP, D, DM, DS)
        val altDMS: String =
            UMS.degreesToDMS(altAz.alt).toString(DP, D, DM, DS)
        val azHMS: String =
            UMS.degreesToHMS(altAz.azm).toString(DP, H, DM, DS)
        return ("<HTML>" +
                "RA: " + raHMS + "<BR>" +
                "DEC: " + decDMS + "<BR>" +
                "ALT: " + altDMS + "<BR>" +
                "AZM: " + azHMS + "<BR>" +
                "Visual Magnitude: " + d.visMag +
                "</HTML>")
    }

    fun getStatusBarText(d: DSO, sky: Sky): String {
        val equ = d.equatorialCoords
        val altAz = sky.EquToAltAzm(equ.rA, equ.dec)
        val D = "deg "
        val DM = "' "
        val DS = "\""
        val H = "h "
        val M = "m "
        val S = "s"
        val SEP = ""
        val DP = 1 // Number of decimal places.
        val raHMS: String = UMS.degreesToHMS(equ.rA).toString(DP, H, M, S)
        val decDMS: String =
            UMS.degreesToDMS(equ.dec).toString(DP, D, DM, DS)
        val altDMS: String =
            UMS.degreesToDMS(altAz.alt).toString(DP, D, DM, DS)
        val azHMS: String =
            UMS.degreesToHMS(altAz.azm).toString(DP, H, DM, DS)
        return ("RA: " + raHMS + ", " +
                "DEC: " + decDMS + ", " +
                "ALT: " + altDMS + ", " +
                "AZM: " + azHMS + ", " +
                "Visual Magnitude:" + d.visMag)
    }

    fun contains(d: DSO, p: SP_Point): Boolean {
        val diameter = getDiameter(d)
        val halfDiam = diameter / 2
        val left = d.skyLocation.x - halfDiam
        val top = d.skyLocation.y - halfDiam
        val ovalBounds = SP_Rectangle(left, top, diameter, diameter)
        return ovalBounds.contains(p.x, p.y)
    }

    override fun getDiameter(b: CelestialBody): Double {
       return DIAMETER
    }

    override fun getColour(c: CelestialBody):SP_Colour { return c.rgbColour }


}
